import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Link,
    Typography,
} from '@mui/material';
import TextField from '../components/SecretTextField';
import { LoadingSpinner } from '../components/LoadingSpinner';
import GoogleSignInButton from '../components/GoogleSignInButton';
import WarningDialog from '../components/WarningDialog';
import { useAuth } from '../context/AuthContext';

import { getErrorMessage } from '../services/ApiError';

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, loginWithGoogle } = useAuth();

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const returnTo = location.state?.returnTo || '/';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(returnTo, { replace: true });
        }
    }, [isAuthenticated, navigate, returnTo]);

    const handleGoogleCredential = async (response) => {
        try {
            setLoading(true);
            setError('');
            const result = await loginWithGoogle(response.credential);
            if (result?.requiresProfile) {
                navigate('/register', {
                    state: { googleTicket: result.googleTicket, googleEmail: result.email, returnTo },
                });
                return;
            }
            navigate(returnTo, { replace: true });
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.loginFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!identifier.trim() || !password) {
            setError(t('auth.errors.required'));
            return;
        }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(identifier.trim())) {
            setError(t('auth.errors.invalidEmail'));
            return;
        }
        try {
            setLoading(true);
            setError('');
            await login(identifier.trim(), password);
            navigate(returnTo, { replace: true });
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.loginFailed')));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="app-page auth-page">
            <LoadingSpinner show={loading} />
            <Card className="app-surface auth-card" sx={{ width: { xs: '90%', sm: '25rem' }, backgroundColor: '#16213e', color: '#fff', padding: 2 }}>
                <CardContent>
                    <Typography className="app-page-title" variant="h5" align="center" sx={{ fontWeight: 'bold', marginBottom: 2, color: '#e94560' }}>
                        {t('auth.loginTitle')}
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label={t('auth.identifier')}
                            type="email"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            autoComplete="email"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                        />
                        <TextField
                            label={t('auth.password')}
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <Button type="submit" className="app-btn" variant="contained" fullWidth sx={{ marginTop: 2 }}>
                            {t('auth.login')}
                        </Button>
                    </form>
                    <Divider sx={{ my: 2, color: '#aaa' }}>{t('auth.or')}</Divider>
                    <GoogleSignInButton onCredential={handleGoogleCredential} />
                    <Box className="auth-links" sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                        <Link component={RouterLink} to="/forgot-password" sx={{ color: '#e94560' }}>
                            {t('auth.forgotPassword')}
                        </Link>
                        <Link component={RouterLink} to="/register" state={{ returnTo }} sx={{ color: '#e94560' }}>
                            {t('auth.register')}
                        </Link>
                    </Box>
                </CardContent>
            </Card>
            <WarningDialog
                fullWidth={true}
                open={!!error}
                onClose={() => setError('')}
                title={t('auth.loginTitle')}
                warningText={error}
            />
        </Box>
    );
};

export default Login;
